"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot } from "@radix-ui/react-slot";
import { PanelLeft } from "lucide-react";
import * as React from "react";
import { cn } from "./cn";
import { Button } from "./button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, } from "./sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from "./tooltip";
const SidebarContext = React.createContext(null);
export function useSidebar() {
    const context = React.useContext(SidebarContext);
    if (!context)
        throw new Error("useSidebar must be used within SidebarProvider");
    return context;
}
function useMobile() {
    const [isMobile, setIsMobile] = React.useState(false);
    React.useEffect(() => {
        const media = window.matchMedia("(max-width: 767px)");
        const update = () => setIsMobile(media.matches);
        update();
        media.addEventListener("change", update);
        return () => media.removeEventListener("change", update);
    }, []);
    return isMobile;
}
export function SidebarProvider({ defaultOpen = true, open: openProp, onOpenChange, className, style, children, ...props }) {
    const isMobile = useMobile();
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
    const [openMobile, setOpenMobile] = React.useState(false);
    const open = openProp ?? internalOpen;
    const setOpen = React.useCallback((value) => {
        onOpenChange?.(value);
        if (openProp === undefined)
            setInternalOpen(value);
    }, [onOpenChange, openProp]);
    const toggleSidebar = React.useCallback(() => (isMobile ? setOpenMobile((value) => !value) : setOpen(!open)), [isMobile, open, setOpen]);
    React.useEffect(() => {
        const handler = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b") {
                event.preventDefault();
                toggleSidebar();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [toggleSidebar]);
    const value = React.useMemo(() => ({
        state: open ? "expanded" : "collapsed",
        open,
        setOpen,
        openMobile,
        setOpenMobile,
        isMobile,
        toggleSidebar,
    }), [isMobile, open, openMobile, setOpen, toggleSidebar]);
    return (_jsx(SidebarContext.Provider, { value: value, children: _jsx(TooltipProvider, { children: _jsx("div", { "data-slot": "sidebar-wrapper", "data-state": open ? "expanded" : "collapsed", className: cn("group/sidebar-wrapper flex min-h-svh w-full bg-sidebar", className), style: {
                    "--sidebar-width": "16rem",
                    "--sidebar-width-icon": "4rem",
                    ...style,
                }, ...props, children: children }) }) }));
}
export function Sidebar({ side = "left", variant = "sidebar", collapsible = "offcanvas", className, children, ...props }) {
    const { isMobile, openMobile, setOpenMobile, state } = useSidebar();
    if (collapsible === "none")
        return (_jsx("aside", { "data-slot": "sidebar", className: cn("flex h-svh w-[var(--sidebar-width)] flex-col bg-sidebar text-sidebar-foreground", className), ...props, children: children }));
    if (isMobile)
        return (_jsx(Sheet, { open: openMobile, onOpenChange: setOpenMobile, children: _jsxs(SheetContent, { "data-sidebar": "sidebar", side: side, className: cn("w-[18rem] p-0", className), showCloseButton: false, children: [_jsxs(SheetHeader, { className: "sr-only", children: [_jsx(SheetTitle, { children: "Navigation" }), _jsx(SheetDescription, { children: "Mobile admin navigation" })] }), _jsx("div", { className: "flex h-full w-full flex-col", children: children })] }) }));
    const hidden = collapsible === "offcanvas" && state === "collapsed";
    return (_jsx("aside", { "data-slot": "sidebar", "data-state": state, "data-variant": variant, "data-side": side, className: cn("sticky top-0 hidden h-svh shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width,transform] duration-200 md:flex", variant === "floating"
            ? "m-2 h-[calc(100svh-1rem)] rounded-xl border shadow-sm"
            : "", hidden
            ? "w-0 -translate-x-full overflow-hidden"
            : state === "collapsed" && collapsible === "icon"
                ? "w-[var(--sidebar-width-icon)]"
                : "w-[var(--sidebar-width)]", side === "right" ? "order-2 border-l border-r-0" : "", className), ...props, children: children }));
}
export function SidebarInset({ className, ...props }) {
    return (_jsx("main", { "data-slot": "sidebar-inset", className: cn("relative flex min-w-0 flex-1 flex-col bg-background text-foreground", className), ...props }));
}
export function SidebarTrigger({ className, onClick, ...props }) {
    const { toggleSidebar } = useSidebar();
    return (_jsxs(Button, { "data-slot": "sidebar-trigger", variant: "ghost", size: "icon", className: cn("size-8", className), onClick: (event) => {
            onClick?.(event);
            toggleSidebar();
        }, ...props, children: [_jsx(PanelLeft, {}), _jsx("span", { className: "sr-only", children: "Toggle sidebar" })] }));
}
export function SidebarRail({ className, ...props }) {
    const { toggleSidebar } = useSidebar();
    return (_jsx("button", { "data-slot": "sidebar-rail", "aria-label": "Toggle sidebar", tabIndex: -1, onClick: toggleSidebar, title: "Toggle sidebar", className: cn("absolute inset-y-0 -right-2 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-px hover:after:bg-sidebar-border sm:flex", className), ...props }));
}
export function SidebarHeader({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sidebar-header", className: cn("flex flex-col gap-2 p-2", className), ...props }));
}
export function SidebarFooter({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sidebar-footer", className: cn("mt-auto flex flex-col gap-2 p-2", className), ...props }));
}
export function SidebarContent({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sidebar-content", className: cn("flex min-h-0 flex-1 flex-col gap-2 overflow-auto", className), ...props }));
}
export function SidebarGroup({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sidebar-group", className: cn("relative flex w-full min-w-0 flex-col p-2", className), ...props }));
}
export function SidebarGroupLabel({ className, asChild = false, ...props }) {
    const Component = asChild ? Slot : "div";
    return (_jsx(Component, { "data-slot": "sidebar-group-label", className: cn("flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 transition-[margin,opacity] group-data-[state=collapsed]/sidebar-wrapper:-mt-8 group-data-[state=collapsed]/sidebar-wrapper:opacity-0", className), ...props }));
}
export function SidebarGroupContent({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sidebar-group-content", className: cn("w-full text-sm", className), ...props }));
}
export function SidebarMenu({ className, ...props }) {
    return (_jsx("ul", { "data-slot": "sidebar-menu", className: cn("flex w-full min-w-0 flex-col gap-1", className), ...props }));
}
export function SidebarMenuItem({ className, ...props }) {
    return (_jsx("li", { "data-slot": "sidebar-menu-item", className: cn("group/menu-item relative", className), ...props }));
}
export function SidebarMenuButton({ asChild = false, isActive = false, size = "default", tooltip, className, children, ...props }) {
    const Component = asChild ? Slot : "button";
    const button = (_jsx(Component, { "data-slot": "sidebar-menu-button", "data-active": isActive, "data-size": size, className: cn("flex w-full items-center gap-2 overflow-hidden rounded-md text-left outline-none transition hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring disabled:pointer-events-none disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground group-data-[state=collapsed]/sidebar-wrapper:size-9 group-data-[state=collapsed]/sidebar-wrapper:justify-center group-data-[state=collapsed]/sidebar-wrapper:px-0 [&>span:last-child]:truncate [&_svg]:size-4 [&_svg]:shrink-0", size === "sm" && "h-8 px-2 text-xs", size === "default" && "h-9 px-2 text-sm", size === "lg" && "h-12 px-2 text-sm", className), ...props, children: children }));
    return tooltip ? (_jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: button }), _jsx(TooltipContent, { side: "right", children: tooltip })] })) : (button);
}
export function SidebarMenuBadge({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sidebar-menu-badge", className: cn("pointer-events-none absolute right-2 top-1/2 flex h-5 min-w-5 -translate-y-1/2 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums group-data-[state=collapsed]/sidebar-wrapper:hidden", className), ...props }));
}
export function SidebarSeparator({ className, ...props }) {
    return (_jsx("hr", { "data-slot": "sidebar-separator", className: cn("mx-2 border-0 border-t border-sidebar-border", className), ...props }));
}
//# sourceMappingURL=sidebar.js.map